import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  Notification,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import { Product } from "@transfr-inc/dashboard-components/constants";
import { useUnloadWarning } from "@transfr-inc/dashboard-components/hooks";

import { useTrekExport } from "@lib/trek-export.hook";
import {
  useOrganizationDetails,
  useOrganizations,
} from "@queries/organization";
import { useContainer } from "@src/contexts/container-context";
import { useCurrentUser } from "@src/hooks/queries/account";
import { useRole } from "@src/hooks/queries/account-selectors";
import { useClassrooms } from "@src/hooks/queries/organization";
import { useStudents } from "@src/hooks/queries/organization-selectors";
import { useAppStore } from "@src/stores/zustand/app-store";

import { useTopCareersSelected } from "@components/top-careers-selected";
import { getTrekDashboardDailyStats, getTrekDashboardStats } from "@services";
import { DashboardBanner, TrekInsights } from "@views/dashboard/components";

export const TrekTab = () => {
  const { trekService, organizationService } = useContainer();
  const currentUser = useCurrentUser();
  const role = useRole();
  const { organizationCode, userId } = currentUser || {};
  const users = useStudents({ role, orgCode: organizationCode, userId });
  const { data: classrooms = [] } = useClassrooms({
    userId,
    role,
    orgCode: organizationCode,
  });
  const { data: organizations = [] } = useOrganizations(organizationCode);
  const setDashboardLoaded = useAppStore((state) => state.setDashboardLoaded);

  const [selectedClassrooms, setSelectedClassrooms] = useState();
  const [dateRange, setDateRange] = useState({});
  const [generalStats, setGeneralStats] = useState({ isLoading: true });
  const [dailyStats, setDailyStats] = useState({ isLoading: true });

  const { orgCode = organizationCode } = useParams();
  const { data: organization } = useOrganizationDetails(orgCode);

  const {
    exportTrekData,
    isLoading: exportingData,
    hasError: exportDataError,
    resetStatus,
  } = useTrekExport({ organizations, classrooms });

  useUnloadWarning({ trigger: exportingData });

  const clearStats = () => {
    setDailyStats({ isLoading: true });
    setGeneralStats({ isLoading: true });
  };

  const doApplyFilters = ({ classrooms, dateRange }) => {
    const { startDate, endDate } = dateRange || {};
    clearStats();
    setDateRange({ startDate, endDate });
    setSelectedClassrooms(classrooms?.map((c) => c.data));
  };

  const getTrekStats = ({ orgCode, filters }) => {
    getTrekDashboardStats({
      trekService,
      organizationService,
      orgCode,
      role,
      userId,
      filters,
    })
      .then((response) => setGeneralStats({ isLoading: false, response }))
      .catch(() => {
        setGeneralStats({ isLoading: false });
      });
    getTrekDashboardDailyStats({
      trekService,
      organizationService,
      orgCode,
      role,
      userId,
      filters,
    })
      .then((response) => setDailyStats({ isLoading: false, response }))
      .catch(() => {
        setDailyStats({ isLoading: false });
      });
  };

  const onExportData = () => {
    const orgCode = organization?.code;
    const filters = {
      classroomIds: selectedClassrooms?.map((c) => c.classroomId),
      dateRange,
    };
    const prefixFileName = selectedClassrooms
      ? selectedClassrooms.map((c) => c.name).join(".")
      : organization?.name;

    exportTrekData({
      orgCode,
      filters,
      prefixFileName,
      role,
      userId,
      isMultiOrg: false,
    });
  };

  const {
    topCareers,
    loading,
    refreshData: getTopCareers,
  } = useTopCareersSelected();

  useEffect(() => {
    const { startDate, endDate } = dateRange;
    const orgCode = organization?.code;
    const filters = {
      classroomIds: selectedClassrooms?.map((c) => c.classroomId),
      dateRange,
    };

    if (startDate && endDate) {
      Promise.allSettled([
        getTrekStats({
          orgCode,
          filters,
        }),
        getTopCareers({
          orgCode,
          filters,
        }),
      ]);
    }
  }, [dateRange, organization?.code, selectedClassrooms]);

  useEffect(() => {
    if (!generalStats.isLoading && !dailyStats.isLoading) {
      setDashboardLoaded();
    }
  }, [generalStats, dailyStats]);

  // Cancel ongoing requests when changing tab
  useEffect(() => () => trekService.cancelRequest(), []);

  return (
    <div className="trek-single-tab">
      <DashboardBanner product={Product.TRK} />
      <TrekInsights
        userInfo={{ userId, role }}
        orgCode={organization?.code}
        generalStats={generalStats?.response}
        dailyStats={dailyStats}
        onApplyFilters={doApplyFilters}
        classroomFilterEnabled
        topCareers={topCareers}
        isLoadingTopCareers={loading}
        users={users}
        onExportData={onExportData}
        exportDataDisabled={exportingData}
      />
      <div className="notification-loader-container">
        {exportingData && (
          <Notification
            type={NotificationType.info}
            closable
            icon={["fa-solid", "spinner"]}
            iconConfig={{ spin: true }}
          >
            We’re preparing a download for Trek insights. Please wait...
          </Notification>
        )}
        {exportDataError && (
          <Notification
            type={NotificationType.error}
            closable
            animated
            onClose={resetStatus}
          >
            Something when wrong. Please try again.
          </Notification>
        )}
      </div>
    </div>
  );
};
